import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WorkspaceDialogService } from 'app/layout/common/workspace-tabs/overlay/workspace-dialog.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { GlobalShortcutsService } from 'app/shared/services/global-shortcuts.service';
import { shellNavigationUrlFromNavItem } from 'app/layout/common/workspace-tabs/workspace-tab-shell-navigation.util';
import { WorkspaceTabsService } from 'app/layout/common/workspace-tabs/workspace-tabs.service';
import {
    CommandPaletteComponent,
    CommandPaletteDialogData,
} from './command-palette.component';
import {
    CommandPaletteEntry,
    filterCommandPaletteEntries,
    flattenNavigationItems,
} from './command-palette.logic';
import { PageCommandsService } from '../shared/page-commands.service';
import { AppCommandsService } from '../shared/app-commands.service';

/**
 * Ctrl/Cmd+K palette.
 * Pages come from the navigation tree already filtered by the backend for this user;
 * commands come from the foreground page. Choosing a page uses the same workspace-tab
 * path as the mega menu.
 */
@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
    private readonly _navigation = inject(NavigationService);
    private readonly _dialog = inject(MatDialog);
    private readonly _globalShortcuts = inject(GlobalShortcutsService);
    private readonly _workspaceTabs = inject(WorkspaceTabsService);
    private readonly _pageCommands = inject(PageCommandsService);
    private readonly _appCommands = inject(AppCommandsService);
    private readonly _workspaceDialogs = inject(WorkspaceDialogService);

    private _navigationEntries: CommandPaletteEntry[] = [];
    private _paletteRef: MatDialogRef<CommandPaletteComponent, CommandPaletteEntry> | null = null;

    constructor() {
        this._navigation.navigation$.pipe(takeUntilDestroyed()).subscribe((navigation) => {
            const tree = navigation?.horizontal?.length
                ? navigation.horizontal
                : (navigation?.default ?? []);
            this._navigationEntries = flattenNavigationItems(
                tree,
                shellNavigationUrlFromNavItem,
                (item) => this._workspaceTabs.navigateFromNavItem(item),
            );
        });
    }

    /**
     * Open the palette in the foreground workspace tab. No-op on auth screens, while the palette
     * is already showing, and while a dialog is visible in this tab, so workflow dialogs keep
     * F10/Escape. Dialogs hidden with a background tab do not block it.
     */
    open(): void {
        if (!this._globalShortcuts.isAppLevelShortcutContextActive()) {
            return;
        }
        const previous = this._paletteRef;
        if (previous && this._dialog.openDialogs.includes(previous)) {
            if (!this._workspaceDialogs.isSuspended(previous)) {
                return;
            }
            // Left open in another tab and hidden there: replace it with one in this tab.
            previous.close();
        }
        const blocked = this._dialog.openDialogs.some(
            (ref) => ref !== previous && !this._workspaceDialogs.isSuspended(ref),
        );
        if (blocked) {
            return;
        }

        // Built once per open so selected-record commands cannot act on a stale row.
        const page = this._pageCommands.activePage();
        const pageCommands = page.commands;
        const appCommands = this._appCommands.activeCommands();

        const ref = this._dialog.open<
            CommandPaletteComponent,
            CommandPaletteDialogData,
            CommandPaletteEntry
        >(CommandPaletteComponent, {
            panelClass: 'command-palette-panel',
            ariaLabel: 'Search pages and actions',
            autoFocus: false,
            restoreFocus: true,
            width: '560px',
            maxWidth: '92vw',
            position: { top: '12vh' },
            data: {
                selectionLabel: page.selectionLabel,
                filter: (query: string) =>
                    filterCommandPaletteEntries(
                        pageCommands,
                        this._navigationEntries,
                        query,
                        appCommands,
                    ),
            },
        });

        this._paletteRef = ref;
        // Switching workspace tabs closes the palette instead of hiding it in the old tab.
        this._workspaceDialogs.markNonResumable(ref);

        // Run after close so MatDialog's focus restore cannot steal focus from a field command.
        // A tab-switch dismiss closes with a string, not an entry.
        ref.afterClosed().subscribe((entry) => {
            if (this._paletteRef === ref) {
                this._paletteRef = null;
            }
            if (typeof entry?.run === 'function') {
                entry.run();
            }
        });
    }
}
